import { ILocationSettings } from "../../models/data.interface";
import { AutocompleteProvider } from "../../types/enums";
import { GeoServiceProviderName } from "../../types/geo-service-provider-name";
import { GoogleMapsLoader } from "../maps/google/google-maps-loader";
import { IGeoServiceApiKeys } from "../geo-service-api-keys";
import { IAddressSuggestProvider, IGeocodingProvider } from "./address-search-provider.interface";
import { GeoSearchChain } from "./geo-search-chain";
import { GoogleAddressProvider } from "./google/google-address-provider";
import { HereAddressProvider } from "./here/here-address-provider";
import { MapboxAddressProvider } from "./mapbox/mapbox-address-provider";

/**
 * `providerId` is the resolved primary even when no chain could be built — "none" is a valid
 * Setting 275 answer, and the caller still needs to know which provider was chosen.
 */
export type GeoSearchSelection = {
    providerId: AutocompleteProvider,
    chain: GeoSearchChain | null
};

type AddressProvider = IAddressSuggestProvider & IGeocodingProvider;

export class GeoSearchProviderFactory {
    /**
     * Setting 275 names a provider per capability plus a failover for each; Setting 274 covers
     * brands that predate it. Geocoding falls back to the autocomplete provider when unset.
     */
    public static createSelection(
        locationSettings: ILocationSettings | null | undefined,
        brandProviderId: AutocompleteProvider,
        apiKeys: IGeoServiceApiKeys
    ): GeoSearchSelection {
        const providerId = this._replaceAdoraWithGoogle(
            this.getProviderId(locationSettings?.autocompleteProvider ?? null, brandProviderId)
        );
        const failoverId = this._replaceAdoraWithGoogle(
            this.getProviderId(locationSettings?.autocompleteProviderFailover ?? null, AutocompleteProvider.NONE)
        );

        const geocodingId = this.getProviderId(locationSettings?.geocodingProvider ?? null, providerId);
        const geocodingFailoverId = this.getProviderId(locationSettings?.geocodingProviderFailover ?? null, failoverId);

        const suggestProviders = this._createProviders([providerId, failoverId], apiKeys);
        const geocodingProviders = this._createProviders([geocodingId, geocodingFailoverId], apiKeys);

        const hasProviders = suggestProviders.length > 0 || geocodingProviders.length > 0;

        return {
            providerId,
            chain: hasProviders ? new GeoSearchChain(suggestProviders, geocodingProviders) : null
        };
    }

    /**
     * Geocodio is a valid Setting 275 value with no OLO3 provider, so it resolves to NONE
     * rather than silently falling back to the older setting.
     */
    public static getProviderId(
        locationProvider: GeoServiceProviderName | null,
        fallbackProviderId: AutocompleteProvider
    ): AutocompleteProvider {
        switch (locationProvider) {
            case "adora":
                return AutocompleteProvider.ADORA;
            case "google":
                return AutocompleteProvider.GOOGLE;
            case "here":
                return AutocompleteProvider.HERE;
            case "mapbox":
                return AutocompleteProvider.MAPBOX;
            case "geocodio":
                return AutocompleteProvider.NONE;
            default:
                return fallbackProviderId;
        }
    }

    public static getApiKey(providerId: AutocompleteProvider, apiKeys: IGeoServiceApiKeys): string {
        switch (providerId) {
            case AutocompleteProvider.GOOGLE:
                return apiKeys.google;
            case AutocompleteProvider.HERE:
                return apiKeys.here;
            case AutocompleteProvider.MAPBOX:
                return apiKeys.mapbox;
            default:
                return "";
        }
    }

    // Adora autocomplete posts every brand zip to GetStreets per keystroke; Google stands in until it scales.
    private static _replaceAdoraWithGoogle(providerId: AutocompleteProvider): AutocompleteProvider {
        return providerId === AutocompleteProvider.ADORA ? AutocompleteProvider.GOOGLE : providerId;
    }

    private static _createProviders(
        providerIds: AutocompleteProvider[],
        apiKeys: IGeoServiceApiKeys
    ): AddressProvider[] {
        const uniqueIds = Array.from(new Set(providerIds));

        return uniqueIds
            .map(providerId => this._createProvider(providerId, apiKeys))
            .filter((provider): provider is AddressProvider => provider !== null);
    }

    // A provider without its own key cannot answer, so it is left out rather than added and
    // failed over on the first call.
    private static _createProvider(
        providerId: AutocompleteProvider,
        apiKeys: IGeoServiceApiKeys
    ): AddressProvider | null {
        const apiKey = this.getApiKey(providerId, apiKeys);
        if (!apiKey)
            return null;

        switch (providerId) {
            case AutocompleteProvider.GOOGLE:
                return new GoogleAddressProvider(apiKey, GoogleMapsLoader.getLoader(apiKey));
            case AutocompleteProvider.HERE:
                return new HereAddressProvider(apiKey);
            case AutocompleteProvider.MAPBOX:
                return new MapboxAddressProvider(apiKey);
            default:
                return null;
        }
    }
}
